import { decodeBase32, generateSecret } from "./base32.ts";
import { buildOtpAuthUri } from "./otpauth.ts";
import { encodeQr, qrToSvgElement } from "./qrcode.ts";
import {
  generateTotp,
  parseAlgorithm,
  secondsRemaining,
  type HashAlgorithm,
} from "./totp.ts";
import { parseInitialUrlSettings, pickOption } from "./url-settings.ts";
import "./styles/app.css";

type Elements = {
  secret: HTMLInputElement;
  issuer: HTMLInputElement;
  account: HTMLInputElement;
  digits: HTMLSelectElement;
  period: HTMLSelectElement;
  algorithm: HTMLSelectElement;
  token: HTMLElement;
  remaining: HTMLElement;
  periodLabel: HTMLElement;
  progress: HTMLProgressElement;
  error: HTMLElement;
  secretWarning: HTMLElement;
  qr: HTMLElement;
  uri: HTMLElement;
  generateSecret: HTMLButtonElement;
  copyToken: HTMLButtonElement;
  copyUri: HTMLButtonElement;
  themeToggle: HTMLButtonElement;
};

type Settings = {
  secret: string;
  issuer: string;
  account: string;
  digits: number;
  period: number;
  algorithm: HashAlgorithm;
};

/** Values worth keeping between ticks. Everything else is read from the DOM. */
type Outputs = {
  /** Last generated code; empty whenever there is nothing safe to copy. */
  token: string;
  /** Last rendered otpauth URI; empty whenever there is nothing safe to copy. */
  uri: string;
  /** Bumped to abandon in-flight work whose result is no longer wanted. */
  generation: number;
};

/** Also read by `public/theme-init.js`, which applies the theme before first paint. */
const THEME_KEY = "totp-theme";

/** Recommended minimum secret size (RFC 4226 §4 suggests ≥128 bits; we use 160). */
const MIN_SECRET_BITS = 160;

const EMPTY_TOKEN = "------";

/** Free-text fields, filled straight from the URL. */
const TEXT_FIELDS = ["secret", "issuer", "account"] as const;

/** Choice fields, whose `<option>` lists define the only legal values. */
const SELECT_FIELDS = ["digits", "period", "algorithm"] as const;

function el<T extends HTMLElement>(id: string, Ctor: new () => T): T {
  const found = document.getElementById(id);
  if (!(found instanceof Ctor)) {
    throw new Error(`#${id} is missing or is not a ${Ctor.name}`);
  }
  return found;
}

function getElements(): Elements {
  return {
    secret: el("secret", HTMLInputElement),
    issuer: el("issuer", HTMLInputElement),
    account: el("account", HTMLInputElement),
    digits: el("digits", HTMLSelectElement),
    period: el("period", HTMLSelectElement),
    algorithm: el("algorithm", HTMLSelectElement),
    token: el("token", HTMLElement),
    remaining: el("remaining", HTMLElement),
    periodLabel: el("period-label", HTMLElement),
    progress: el("progress", HTMLProgressElement),
    error: el("error", HTMLElement),
    secretWarning: el("secret-warning", HTMLElement),
    qr: el("qr", HTMLElement),
    uri: el("uri", HTMLElement),
    generateSecret: el("generate-secret", HTMLButtonElement),
    copyToken: el("copy-token", HTMLButtonElement),
    copyUri: el("copy-uri", HTMLButtonElement),
    themeToggle: el("theme-toggle", HTMLButtonElement),
  };
}

/** Show `text`, or hide the element entirely when it is empty. */
function setMessage(target: HTMLElement, text: string): void {
  target.hidden = !text;
  target.textContent = text;
}

// —— theme ——

function isDarkTheme(): boolean {
  return document.documentElement.dataset["theme"] === "dark";
}

/** Reflect the theme in the document and the toggle. Which icon shows is CSS's job. */
function renderTheme(dark: boolean, button: HTMLButtonElement): void {
  if (dark) {
    document.documentElement.dataset["theme"] = "dark";
  } else {
    delete document.documentElement.dataset["theme"];
  }
  button.setAttribute(
    "aria-label",
    dark ? "Switch to light theme" : "Switch to dark theme",
  );
  button.title = dark ? "Light theme" : "Dark theme";
}

function initTheme(button: HTMLButtonElement): void {
  // theme-init.js already applied the stored preference, so the DOM is the source
  // of truth here — no second read, and nothing is written until the user chooses.
  renderTheme(isDarkTheme(), button);

  button.addEventListener("click", () => {
    const dark = !isDarkTheme();
    renderTheme(dark, button);
    try {
      localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch {
      /* private mode / blocked storage */
    }
  });
}

// —— settings ——

/** Take `value` only if this `<select>` offers it; the markup owns the legal set. */
function applyToSelect(select: HTMLSelectElement, value: string | null): void {
  const offered = [...select.options].map((option) => option.value);
  const picked = pickOption(offered, value);
  if (picked !== null) {
    select.value = picked;
  }
}

/**
 * Load non-secret settings from the query string, and the secret only from the
 * fragment. Scrub all URL data immediately after reading it so secrets from
 * obsolete `?key=` links, metadata, and fragments do not linger in history,
 * referrers, or copied URLs. Query-string secrets are intentionally unsupported.
 */
function readParamsFromUrl(els: Elements): void {
  const settings = parseInitialUrlSettings(location.search, location.hash);

  for (const field of TEXT_FIELDS) {
    const value = settings[field];
    if (value) {
      els[field].value = value;
    }
  }
  for (const field of SELECT_FIELDS) {
    applyToSelect(els[field], settings[field]);
  }

  if (location.search || location.hash) {
    try {
      history.replaceState(null, "", location.pathname);
    } catch {
      // Ignore restrictions in unusual embedded or locked-down browser contexts.
    }
  }
}

/**
 * Read the form. Lengths are already bounded by `maxlength` in the markup and by
 * `parseInitialUrlSettings` for URL input, which are the only ways a value arrives.
 */
function currentSettings(els: Elements): Settings {
  return {
    secret: els.secret.value.trim(),
    issuer: els.issuer.value.trim(),
    account: els.account.value.trim(),
    digits: Number(els.digits.value),
    period: Number(els.period.value),
    algorithm: parseAlgorithm(els.algorithm.value),
  };
}

// —— output ——

/** A copy button works exactly when there is something to copy. */
function syncCopyButtons(els: Elements, out: Outputs): void {
  els.copyToken.disabled = !out.token;
  els.copyUri.disabled = !out.uri;
}

/**
 * Write the code only when it actually changes. `#token` is an aria-live region
 * and `tick` runs every second, so an unconditional write replaces the text node
 * once a second and can have assistive tech re-announce an unchanged code.
 */
function setToken(els: Elements, text: string): void {
  if (els.token.textContent !== text) {
    els.token.textContent = text;
  }
}

function clearOutputs(els: Elements, out: Outputs): void {
  out.token = "";
  out.uri = "";
  setToken(els, EMPTY_TOKEN);
  els.uri.textContent = "";
  els.qr.replaceChildren();
  syncCopyButtons(els, out);
}

function showError(els: Elements, message: string): void {
  setMessage(els.error, message);
  els.token.classList.toggle("is-invalid", Boolean(message));
}

function secretWarningText(secret: string): string {
  if (!secret) {
    return "";
  }
  try {
    const bits = decodeBase32(secret).length * 8;
    if (bits > 0 && bits < MIN_SECRET_BITS) {
      return `This secret is only ${bits} bits. Prefer at least ${MIN_SECRET_BITS} bits (use Regenerate for a strong random secret).`;
    }
  } catch {
    // Invalid Base32 is reported by the main error path; no extra warning.
  }
  return "";
}

function renderQr(els: Elements, out: Outputs, uri: string): void {
  if (uri === out.uri) {
    return;
  }
  els.uri.textContent = uri;

  try {
    const matrix = encodeQr(uri, { ecc: "M", margin: 2 });
    els.qr.replaceChildren(qrToSvgElement(matrix, "QR code for authenticator import"));
    out.uri = uri;
  } catch (err) {
    els.qr.replaceChildren();
    els.uri.textContent = err instanceof Error ? err.message : "QR encode failed";
    out.uri = "";
  }
  syncCopyButtons(els, out);
}

async function tick(els: Elements, out: Outputs): Promise<void> {
  const generation = ++out.generation;
  const settings = currentSettings(els);
  const remaining = secondsRemaining(settings.period);

  els.remaining.textContent = String(remaining);
  els.periodLabel.textContent = `/ ${settings.period}s`;
  els.progress.max = settings.period;
  els.progress.value = remaining;
  setMessage(els.secretWarning, secretWarningText(settings.secret));

  if (!settings.secret) {
    showError(els, "Enter a Base32 secret (or generate one).");
    clearOutputs(els, out);
    return;
  }

  try {
    const token = await generateTotp(settings);
    if (generation !== out.generation) {
      return;
    }
    showError(els, "");
    out.token = token;
    setToken(els, token);
    renderQr(els, out, buildOtpAuthUri(settings));
    syncCopyButtons(els, out);
  } catch (err) {
    if (generation !== out.generation) {
      return;
    }
    showError(els, err instanceof Error ? err.message : "Failed to generate TOTP");
    clearOutputs(els, out);
  }
}

async function copyText(
  text: string,
  button: HTMLButtonElement,
  label: string,
): Promise<void> {
  let outcome = "Copied!";
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    outcome = "Copy failed";
  }
  button.textContent = outcome;
  window.setTimeout(() => {
    button.textContent = label;
  }, 1200);
}

/** Empty field → Generate; anything else → Regenerate (including auto-filled secrets). */
function syncGenerateLabel(els: Elements): void {
  const hasSecret = Boolean(els.secret.value.trim());
  els.generateSecret.textContent = hasSecret ? "Regenerate" : "Generate";
  els.generateSecret.title = hasSecret
    ? "Regenerate random 160-bit secret"
    : "Generate random 160-bit secret";
}

function bind(els: Elements, out: Outputs): void {
  const refresh = (): void => {
    // Drop previous codes as soon as inputs change so Copy cannot leak them.
    clearOutputs(els, out);
    syncGenerateLabel(els);
    void tick(els, out);
  };

  for (const field of [...TEXT_FIELDS, ...SELECT_FIELDS]) {
    els[field].addEventListener("input", refresh);
    els[field].addEventListener("change", refresh);
  }

  els.generateSecret.addEventListener("click", () => {
    els.secret.value = generateSecret(20);
    refresh();
  });

  // Both buttons are disabled whenever their value is empty, so no extra guard.
  els.copyToken.addEventListener("click", () => {
    void copyText(out.token, els.copyToken, "Copy code");
  });
  els.copyUri.addEventListener("click", () => {
    void copyText(out.uri, els.copyUri, "Copy secret URI");
  });
}

function main(): void {
  const els = getElements();
  const out: Outputs = { token: "", uri: "", generation: 0 };

  initTheme(els.themeToggle);
  readParamsFromUrl(els);
  // Fresh random secret when none was supplied via the fragment. Avoids a
  // blank page and the well-known RFC demo key that people might enroll by habit.
  if (!els.secret.value.trim()) {
    els.secret.value = generateSecret(20);
  }
  syncGenerateLabel(els);
  bind(els, out);
  void tick(els, out);
  window.setInterval(() => {
    void tick(els, out);
  }, 1000);

  window.addEventListener("pagehide", () => {
    // Prevent back/forward cache from retaining sensitive form and output state.
    out.generation++;
    for (const field of TEXT_FIELDS) {
      els[field].value = "";
    }
    setMessage(els.secretWarning, "");
    clearOutputs(els, out);
    syncGenerateLabel(els);
  });
}

main();
